import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import AdminLayout from './AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { promptLibraryKeys } from '@/hooks/usePromptLibrary';
import {
  Plus,
  Edit,
  Archive,
  Copy,
  FolderOpen,
  FileText,
  RefreshCw,
  Search,
  Variable,
  Eye,
  AlertTriangle,
  RotateCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  substituteVariables,
  getExampleValues,
  VARIABLE_CATEGORIES,
  type PromptVariableDefinition,
  type VariableCategory,
} from '../../../../shared/promptVariables';
import { VariablePillEditor, type VariablePillEditorRef } from '@/components/admin/VariablePillEditor';

// Types
interface Category {
  id: number;
  slug: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isSystemCategory: boolean;
  templateCount: number;
  createdAt: string;
  updatedAt: string;
}

// T-379: Surface types for suggested actions
type PromptSurface = 'threads' | 'client' | 'trip';

interface Template {
  id: number;
  categoryId: number | null;
  title: string;
  description: string | null;
  bodyTemplate: string;
  isActive: boolean;
  isSystemDefault: boolean;
  category?: {
    id: number;
    slug: string;
    label: string;
  } | null;
  scope: 'system' | 'user';
  createdAt: string;
  updatedAt: string;
  // T-379: Suggested action metadata
  metadata?: {
    enableSuggestedActions?: boolean;
    suggestedSurfaces?: PromptSurface[];
    tags?: string[];
    usageCount?: number;
    lastUsedAt?: string;
  };
}

type VariablesByCategory = Record<VariableCategory, PromptVariableDefinition[]>;

// Category Dialog Component
function CategoryDialog({
  open,
  onOpenChange,
  category,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category | null;
  onSave: (data: { slug?: string; label: string; description?: string; sortOrder?: number }) => void;
  isSaving: boolean;
}) {
  const [label, setLabel] = useState(category?.label ?? '');
  const [slug, setSlug] = useState(category?.slug ?? '');
  const [description, setDescription] = useState(category?.description ?? '');
  const [sortOrder, setSortOrder] = useState(category?.sortOrder ?? 0);

  // Sync form state when category prop changes (for edit vs create mode)
  useEffect(() => {
    setLabel(category?.label ?? '');
    setSlug(category?.slug ?? '');
    setDescription(category?.description ?? '');
    setSortOrder(category?.sortOrder ?? 0);
  }, [category]);

  const isEditing = !!category;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...(isEditing ? {} : { slug: slug.toLowerCase().replace(/\s+/g, '-') }),
      label,
      description: description || undefined,
      sortOrder,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Category' : 'New Category'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the category details.'
                : 'Create a new category to organize prompts.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="label">Label</Label>
              <Input
                id="label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Marketing & Social"
                required
              />
            </div>
            {!isEditing && (
              <div className="grid gap-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="e.g., marketing-social"
                  pattern="^[a-z0-9]+(-[a-z0-9]+)*$"
                  title="Lowercase letters, numbers, and hyphens only"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  URL-friendly identifier (lowercase, hyphens only)
                </p>
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this category..."
                rows={2}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="sortOrder">Sort Order</Label>
              <Input
                id="sortOrder"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
                min={0}
                max={999}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// T-379: Surface configuration for suggested actions
const SURFACE_OPTIONS: { value: PromptSurface; label: string; description: string }[] = [
  { value: 'threads', label: 'Threads / Command Bar', description: 'Show in the main command bar' },
  { value: 'client', label: 'Client Record', description: 'Show on client detail pages' },
  { value: 'trip', label: 'Trip Canvas', description: 'Show on trip planning canvas' },
];

// Template Dialog Component
function TemplateDialog({
  open,
  onOpenChange,
  template,
  categories,
  variables,
  allVariables,
  onSave,
  isSaving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
  categories: Category[];
  variables: VariablesByCategory;
  allVariables: PromptVariableDefinition[];
  onSave: (data: {
    title: string;
    description?: string;
    bodyTemplate: string;
    categoryId?: number;
    isActive?: boolean;
    metadata?: Record<string, unknown>;
    isPublic?: boolean;
  }) => void;
  isSaving: boolean;
}) {
  const [title, setTitle] = useState(template?.title ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [bodyTemplate, setBodyTemplate] = useState(template?.bodyTemplate ?? '');
  const [categoryId, setCategoryId] = useState<number | undefined>(template?.categoryId ?? undefined);
  const [isPublic, setIsPublic] = useState(template?.scope === 'system');
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef<VariablePillEditorRef>(null);

  // T-379: Suggested action state
  const [enableSuggestedActions, setEnableSuggestedActions] = useState(
    template?.metadata?.enableSuggestedActions ?? false
  );
  const [suggestedSurfaces, setSuggestedSurfaces] = useState<PromptSurface[]>(
    template?.metadata?.suggestedSurfaces ?? []
  );

  // Sync form state when template prop changes (for edit vs create mode)
  useEffect(() => {
    setTitle(template?.title ?? '');
    setDescription(template?.description ?? '');
    setBodyTemplate(template?.bodyTemplate ?? '');
    setCategoryId(template?.categoryId ?? undefined);
    setIsPublic(template?.scope === 'system');
    setShowPreview(false);
    // T-379: Sync suggested action state
    setEnableSuggestedActions(template?.metadata?.enableSuggestedActions ?? false);
    setSuggestedSurfaces(template?.metadata?.suggestedSurfaces ?? []);
  }, [template]);

  const isEditing = !!template;

  const handleSurfaceToggle = (surface: PromptSurface, checked: boolean) => {
    if (checked) {
      setSuggestedSurfaces((prev) => [...prev, surface]);
    } else {
      setSuggestedSurfaces((prev) => prev.filter((s) => s !== surface));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Build metadata object, preserving existing metadata fields
    const existingMetadata = template?.metadata ?? {};
    const metadata = {
      ...existingMetadata,
      enableSuggestedActions,
      suggestedSurfaces: enableSuggestedActions ? suggestedSurfaces : [],
    };
    onSave({
      title,
      description: description || undefined,
      bodyTemplate,
      categoryId,
      metadata,
      isPublic,
    });
  };

  const insertVariable = (key: string) => {
    // Use the editor's insertVariable method if available (cursor-aware)
    if (editorRef.current) {
      editorRef.current.insertVariable(key);
    } else {
      // Fallback: append to the end
      setBodyTemplate((prev) => prev + `{{${key}}}`);
    }
  };

  const previewText = substituteVariables(bodyTemplate, getExampleValues());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Prompt Template' : 'New Prompt Template'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the prompt template details.'
                : 'Create a new system prompt template.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Marketing Email for Cruise Clients"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={categoryId?.toString() ?? 'none'}
                onValueChange={(v) => setCategoryId(v === 'none' ? undefined : parseInt(v, 10))}
              >
                {/* AR-210 (TC-38.1): id matches the Label's htmlFor="category". */}
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select a category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief helper text for users..."
              />
            </div>

            <div className="flex items-start gap-3 rounded-md border p-3 bg-slate-50 dark:bg-gray-800">
              <Checkbox
                id="is-public"
                aria-labelledby="is-public-label"
                aria-describedby="is-public-help"
                checked={isPublic}
                onCheckedChange={(checked) => setIsPublic(checked === true)}
              />
              <div className="space-y-1">
                <Label id="is-public-label" htmlFor="is-public" className="text-sm font-medium">
                  Make this prompt public
                </Label>
                <p id="is-public-help" className="text-xs text-muted-foreground">
                  Public prompts are visible to all users (admin-owned). Uncheck to keep it private to you.
                </p>
              </div>
            </div>

            {/* T-379: Suggested Actions Configuration */}
            <div className="rounded-md border p-4 bg-slate-50 dark:bg-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label htmlFor="enable-suggested" className="text-sm font-medium">
                    Enable for Suggested Actions
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Show this prompt as a suggestion in the command bar and other surfaces
                  </p>
                </div>
                <Switch
                  id="enable-suggested"
                  checked={enableSuggestedActions}
                  onCheckedChange={setEnableSuggestedActions}
                />
              </div>
              {enableSuggestedActions && (
                <div className="pt-3 border-t">
                  <Label className="text-sm font-medium mb-2 block">Show on Surfaces</Label>
                  <div className="space-y-2">
                    {SURFACE_OPTIONS.map((surface) => (
                      <div key={surface.value} className="flex items-start space-x-2">
                        <Checkbox
                          id={`surface-${surface.value}`}
                          checked={suggestedSurfaces.includes(surface.value)}
                          onCheckedChange={(checked) =>
                            handleSurfaceToggle(surface.value, checked === true)
                          }
                        />
                        <div className="grid gap-0.5 leading-none">
                          <label
                            htmlFor={`surface-${surface.value}`}
                            className="text-sm font-medium cursor-pointer"
                          >
                            {surface.label}
                          </label>
                          <p className="text-xs text-muted-foreground">
                            {surface.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label id="prompt-body-label">Prompt Body</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {showPreview ? 'Hide Preview' : 'Preview'}
                </Button>
              </div>
              <VariablePillEditor
                ref={editorRef}
                value={bodyTemplate}
                onChange={setBodyTemplate}
                placeholder="Write your prompt template here. Click variables below to insert them..."
                aria-labelledby="prompt-body-label"
                variables={allVariables}
                minRows={6}
              />
              <p className="text-xs text-muted-foreground">
                Click the variable buttons below to insert them at your cursor position. 
                Variables will appear as colored pills in the editor.
              </p>
            </div>

            {showPreview && bodyTemplate && (
              <div className="rounded-md border bg-muted/50 p-4">
                <p className="text-sm font-medium mb-2">Preview (with example values):</p>
                <p className="text-sm whitespace-pre-wrap">{previewText}</p>
              </div>
            )}

            {/* Variables Panel */}
            <div className="rounded-md border p-4 bg-slate-50 dark:bg-gray-800">
              <div className="flex items-center gap-2 mb-3">
                <Variable className="h-4 w-4" />
                <span className="font-medium text-sm">Available Variables</span>
                <span className="text-xs text-muted-foreground">(click to insert at cursor)</span>
              </div>
              <div className="space-y-3">
                {VARIABLE_CATEGORIES.map((category) => {
                  const vars = variables[category] || [];
                  if (vars.length === 0) return null;
                  return (
                    <div key={category}>
                      <p className="text-xs font-medium text-muted-foreground uppercase mb-1.5">
                        {category}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {vars.map((v: PromptVariableDefinition) => (
                          <Button
                            key={v.key}
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs hover:bg-purple-50 hover:text-purple-700 hover:border-purple-300 dark:hover:bg-purple-900/50 dark:hover:text-purple-300 dark:hover:border-purple-700"
                            onClick={() => insertVariable(v.key)}
                            title={v.description || v.label}
                          >
                            {v.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Page Component
export default function AdminPromptsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  type SelectedCategory = number | 'uncategorized' | null;
  const [selectedCategoryId, setSelectedCategoryId] = useState<SelectedCategory>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Dialog states
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  // Fetch categories
  const { data: categoriesData, isLoading: loadingCategories } = useQuery<{ categories: Category[] }>({
    queryKey: ['/api/admin/prompt-library/categories'],
  });

  // Fetch templates
  const { data: templatesData, isLoading: loadingTemplates } = useQuery<{
    items: Template[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }>({
    queryKey: [
      '/api/admin/prompt-library/templates',
      { categoryId: selectedCategoryId, isActive: showActiveOnly, search: searchTerm },
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCategoryId === 'uncategorized') {
        params.set('categoryId', 'null');
      } else if (typeof selectedCategoryId === 'number') {
        params.set('categoryId', selectedCategoryId.toString());
      }
      if (showActiveOnly) params.set('isActive', 'true');
      if (searchTerm) params.set('search', searchTerm);
      params.set('pageSize', '50');
      const response = await fetch(`/api/admin/prompt-library/templates?${params}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch templates');
      return response.json();
    },
  });

  // Fetch variables
  const { data: variablesData } = useQuery<{
    variables: PromptVariableDefinition[];
    byCategory: VariablesByCategory;
  }>({
    queryKey: ['/api/admin/prompt-library/variables'],
  });

  // Category mutations
  const createCategoryMutation = useMutation({
    mutationFn: (data: { slug: string; label: string; description?: string; sortOrder?: number }) =>
      apiRequest('POST', '/api/admin/prompt-library/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/categories'] });
      setCategoryDialogOpen(false);
      toast({ title: 'Category created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create category', description: error.message, variant: 'destructive' });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { label?: string; description?: string; sortOrder?: number };
    }) => apiRequest('PATCH', `/api/admin/prompt-library/categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/categories'] });
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      toast({ title: 'Category updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update category', description: error.message, variant: 'destructive' });
    },
  });

  // Template mutations
  const createTemplateMutation = useMutation({
    mutationFn: (data: {
      title: string;
      description?: string;
      bodyTemplate: string;
      categoryId?: number | null;
      metadata?: Record<string, unknown>;
      isPublic?: boolean;
    }) => apiRequest('POST', '/api/admin/prompt-library/templates', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/templates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/categories'] });
      // Keep user prompt views in sync (all users)
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.all });
      setTemplateDialogOpen(false);
      toast({ title: 'Template created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to create template', description: error.message, variant: 'destructive' });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: {
        title?: string;
        description?: string;
        bodyTemplate?: string;
        categoryId?: number | null;
        isActive?: boolean;
        metadata?: Record<string, unknown>;
        isPublic?: boolean;
      };
    }) => apiRequest('PATCH', `/api/admin/prompt-library/templates/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/templates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/prompt-library/categories'] });
      // Keep user prompt views in sync (all users)
      queryClient.invalidateQueries({ queryKey: promptLibraryKeys.all });
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      toast({ title: 'Template updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to update template', description: error.message, variant: 'destructive' });
    },
  });

  const categories = categoriesData?.categories ?? [];
  const templates = templatesData?.items ?? [];
  const uncategorizedCount = useMemo(
    () => templates.filter((t) => t.categoryId === null).length,
    [templates],
  );
  const filteredTemplates = useMemo(() => templates, [templates]);
  const totalTemplates = templatesData?.total ?? filteredTemplates.length;
  const variablesByCategory: VariablesByCategory = variablesData?.byCategory ?? { 
    profile: [], 
    business: [], 
    social: [],
    preferences: [] 
  };
  const allVariables = variablesData?.variables ?? [];

  const handleSaveCategory = (data: {
    slug?: string;
    label: string;
    description?: string;
    sortOrder?: number;
  }) => {
    if (editingCategory) {
      updateCategoryMutation.mutate({ id: editingCategory.id, data });
    } else {
      createCategoryMutation.mutate(data as { slug: string; label: string; description?: string; sortOrder?: number });
    }
  };

  const handleSaveTemplate = (data: {
    title: string;
    description?: string;
    bodyTemplate: string;
    categoryId?: number;
    isActive?: boolean;
    metadata?: Record<string, unknown>;
    isPublic?: boolean;
  }) => {
    // Normalize categoryId so clearing a category sends null, not undefined
    const normalizedCategoryId =
      data.categoryId === undefined ? null : data.categoryId;
    const payload = { ...data, categoryId: normalizedCategoryId };

    if (editingTemplate) {
      updateTemplateMutation.mutate({ id: editingTemplate.id, data: payload });
    } else {
      createTemplateMutation.mutate(payload);
    }
  };

  const handleArchiveTemplate = (template: Template) => {
    updateTemplateMutation.mutate({
      id: template.id,
      data: { isActive: false },
    });
  };

  const handleUnarchiveTemplate = (template: Template) => {
    updateTemplateMutation.mutate({
      id: template.id,
      data: { isActive: true },
    });
  };

  const handleDuplicateTemplate = (template: Template) => {
    createTemplateMutation.mutate({
      title: `${template.title} (Copy)`,
      description: template.description ?? undefined,
      bodyTemplate: template.bodyTemplate,
      categoryId: template.categoryId ?? undefined,
      isPublic: template.isSystemDefault,
    });
  };

  const openNewCategory = () => {
    setEditingCategory(null);
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryDialogOpen(true);
  };

  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateDialogOpen(true);
  };

  const openEditTemplate = (template: Template) => {
    setEditingTemplate(template);
    setTemplateDialogOpen(true);
  };

  const isLoading = loadingCategories || loadingTemplates;

  return (
    <AdminLayout
      title="Prompt Library"
      description="Manage system-wide prompt templates for the Threads command bar."
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Categories Sidebar */}
          <div className="col-span-3">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Categories</CardTitle>
                  <Button variant="ghost" size="sm" onClick={openNewCategory} aria-label="Add category">
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-0.5">
                  <button
                    onClick={() => setSelectedCategoryId(null)}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors',
                      'hover:bg-slate-100 dark:hover:bg-gray-700/50',
                      selectedCategoryId === null && 'bg-orange-50 text-orange-900 dark:bg-orange-900/30 dark:text-orange-100 font-medium',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <FolderOpen className="h-4 w-4" />
                      All Prompts
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {templates.length}
                    </Badge>
                  </button>
                  <button
                    onClick={() => setSelectedCategoryId('uncategorized')}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors',
                      'hover:bg-slate-100 dark:hover:bg-gray-700/50',
                      selectedCategoryId === 'uncategorized' && 'bg-orange-50 text-orange-900 dark:bg-orange-900/30 dark:text-orange-100 font-medium',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0" />
                      <span className="truncate">Uncategorized</span>
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {uncategorizedCount}
                    </Badge>
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategoryId(category.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-4 py-2.5 text-left text-sm transition-colors group',
                        'hover:bg-slate-100 dark:hover:bg-gray-700/50',
                        selectedCategoryId === category.id && 'bg-orange-50 text-orange-900 dark:bg-orange-900/30 dark:text-orange-100 font-medium',
                      )}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="truncate">{category.label}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary" className="text-xs">
                          {category.templateCount}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                          aria-label={`Edit category ${category.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditCategory(category);
                          }}
                        >
                          <Edit className="h-3 w-3" aria-hidden="true" />
                        </Button>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Templates List */}
          <div className="col-span-9 space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search prompts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="active-only"
                  checked={showActiveOnly}
                  onCheckedChange={setShowActiveOnly}
                />
                <Label htmlFor="active-only" className="text-sm">
                  Active only
                </Label>
              </div>
              <Button onClick={openNewTemplate}>
                <Plus className="h-4 w-4 mr-2" />
                New Prompt
              </Button>
            </div>

            {/* Templates Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {selectedCategoryId === null
                    ? 'All Prompts'
                    : selectedCategoryId === 'uncategorized'
                      ? 'Uncategorized'
                      : categories.find((c) => c.id === selectedCategoryId)?.label ?? 'Prompts'}
                </CardTitle>
                <CardDescription>
                  {totalTemplates} prompt{totalTemplates !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredTemplates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No prompts found</p>
                    <Button variant="outline" className="mt-4" onClick={openNewTemplate}>
                      <Plus className="h-4 w-4 mr-2" />
                      Create your first prompt
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTemplates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{template.title}</p>
                              {template.description && (
                                <p className="text-sm text-muted-foreground truncate max-w-md">
                                  {template.description}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {template.category ? (
                              <Badge variant="outline">{template.category.label}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {template.isActive ? (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Archived</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditTemplate(template)}
                                aria-label={`Edit ${template.title}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDuplicateTemplate(template)}
                                aria-label={`Duplicate ${template.title}`}
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              {template.isActive ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleArchiveTemplate(template)}
                                  title="Archive"
                                  aria-label={`Archive ${template.title}`}
                                >
                                  <Archive className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleUnarchiveTemplate(template)}
                                  title="Unarchive"
                                  aria-label={`Unarchive ${template.title}`}
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Variables Reference */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Variable className="h-4 w-4" />
                  <CardTitle className="text-base">Variable Reference</CardTitle>
                </div>
                <CardDescription>
                  These variables can be used in prompt templates. Click to copy.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {VARIABLE_CATEGORIES.map((category) => {
                    const vars = variablesByCategory[category] || [];
                    if (vars.length === 0) return null;
                    return (
                      <div key={category}>
                        <p className="text-xs font-medium text-muted-foreground uppercase mb-2">
                          {category}
                        </p>
                        <div className="space-y-1">
                          {vars.map((v: PromptVariableDefinition) => (
                            <div
                              key={v.key}
                              className="flex items-center justify-between text-sm group cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700/50 rounded px-1 -mx-1"
                              title={v.description}
                              onClick={() => navigator.clipboard.writeText(`{{${v.key}}}`)}
                            >
                              <span className="font-mono text-xs bg-slate-100 dark:bg-gray-700 px-1.5 py-0.5 rounded group-hover:bg-purple-100 group-hover:text-purple-700 dark:group-hover:bg-purple-900/50 dark:group-hover:text-purple-300">
                                {`{{${v.key}}}`}
                              </span>
                              <span className="text-muted-foreground text-xs truncate ml-2">{v.label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Category Dialog */}
      <CategoryDialog
        open={categoryDialogOpen}
        onOpenChange={(open) => {
          setCategoryDialogOpen(open);
          if (!open) setEditingCategory(null);
        }}
        category={editingCategory}
        onSave={handleSaveCategory}
        isSaving={createCategoryMutation.isPending || updateCategoryMutation.isPending}
      />

      {/* Template Dialog */}
      <TemplateDialog
        open={templateDialogOpen}
        onOpenChange={(open) => {
          setTemplateDialogOpen(open);
          if (!open) setEditingTemplate(null);
        }}
        template={editingTemplate}
        categories={categories}
        variables={variablesByCategory}
        allVariables={allVariables}
        onSave={handleSaveTemplate}
        isSaving={createTemplateMutation.isPending || updateTemplateMutation.isPending}
      />
    </AdminLayout>
  );
}

